<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import draggable from "vuedraggable";
import Swal from "sweetalert2";
import api from "../../api/axios";
import ArgonButton from "@/components/ArgonButton.vue";
import ArgonInput from "@/components/ArgonInput.vue";

const router = useRouter();

const pipelines = ref([]);
const selectedPipelineId = ref(null);
const stages = ref([]);
const dealsByStage = ref({});
const contactos = ref([]);
const cargando = ref(true);
const lossReasons = ref([]);
const automations = ref([]);

const showStagesModal = ref(false);
const showLossReasonsModal = ref(false);
const showAutomationsModal = ref(false);
const showPipelinesModal = ref(false);
const showDealModal = ref(false);
const isEditDeal = ref(false);
const guardandoDeal = ref(false);

const nuevaEtapa = ref("");
const nuevoMotivo = ref("");
const nuevoPipelineName = ref("");

const nuevaAutomatizacion = ref({
  stageId: "",
  actionType: "create_task",
  message: "",
  title: "",
  dueInDays: 1
});

const dealForm = ref({
  id: null,
  stageId: null,
  contactId: "",
  title: "",
  value: 0,
  priority: "media",
  probability: null,
  commission: 0,
  notes: "",
  products: []
});

const dealFiles = ref([]);
const dealQuotes = ref([]);
const nuevoArchivo = ref({ name: "", url: "" });
const stats = ref(null);

const automationsDelPipeline = computed(() => {
  const stageIds = stages.value.map(s => s.id);
  return automations.value.filter(a => stageIds.includes(a.stage_id));
});

function formatMoneda(valor) {
  return new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 }).format(valor || 0);
}

async function cargarPipelines() {
  const res = await api.get("/crm/pipelines");
  pipelines.value = res.data.pipelines;

  if (!selectedPipelineId.value && pipelines.value.length > 0) {
    selectedPipelineId.value = pipelines.value[0].id;
  }
}

async function cargarContactos() {
  const res = await api.get("/crm/contacts", { params: { limit: 500 } });
  contactos.value = res.data.contacts;
}

async function cargar() {
  if (!selectedPipelineId.value) return;

  const [stagesRes, dealsRes, reasonsRes, automationsRes, statsRes] = await Promise.all([
    api.get("/crm/pipeline/stages", { params: { pipelineId: selectedPipelineId.value } }),
    api.get("/crm/deals", { params: { pipelineId: selectedPipelineId.value } }),
    api.get("/crm/pipeline/loss-reasons"),
    api.get("/crm/pipeline/automations"),
    api.get("/crm/deals/stats", { params: { pipelineId: selectedPipelineId.value } })
  ]);

  stages.value = stagesRes.data.stages;
  lossReasons.value = reasonsRes.data.reasons;
  automations.value = automationsRes.data.automations;
  stats.value = statsRes.data.stats;

  const grupos = {};
  for (const s of stages.value) grupos[s.id] = [];

  for (const d of dealsRes.data.deals) {
    if (d.stage_id && grupos[d.stage_id]) grupos[d.stage_id].push(d);
  }

  dealsByStage.value = grupos;
}

async function onChange(stageId, evt) {
  if (!evt.added) return;

  const deal = evt.added.element;
  const stage = stages.value.find(s => s.id === stageId);

  let lossReasonId = null;
  let lossNote = null;

  if (stage?.is_lost) {
    const opciones = {};
    lossReasons.value.forEach(r => { opciones[r.id] = r.name; });

    const { value: formValues, isConfirmed } = await Swal.fire({
      icon: "question",
      title: "Motivo de pérdida",
      html:
        '<select id="swal-loss-reason" class="swal2-select form-select mb-2"></select>' +
        '<textarea id="swal-loss-note" class="swal2-textarea" placeholder="Nota (opcional)"></textarea>',
      didOpen: () => {
        const select = document.getElementById("swal-loss-reason");
        for (const [id, name] of Object.entries(opciones)) {
          const opt = document.createElement("option");
          opt.value = id;
          opt.textContent = name;
          select.appendChild(opt);
        }
      },
      preConfirm: () => ({
        lossReasonId: Number(document.getElementById("swal-loss-reason").value),
        lossNote: document.getElementById("swal-loss-note").value || null
      }),
      showCancelButton: true,
      confirmButtonText: "Marcar como perdido",
      cancelButtonText: "Cancelar"
    });

    if (!isConfirmed) {
      await cargar();
      return;
    }

    lossReasonId = formValues.lossReasonId;
    lossNote = formValues.lossNote;
  }

  try {
    await api.patch(`/crm/deals/${deal.id}/stage`, { stageId, lossReasonId, lossNote });
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo mover el negocio",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    });
    await cargar();
  }
}

function verContacto(contactId) {
  router.push(`/crm/contacts/${contactId}`);
}

function abrirNuevoNegocio(stageId) {
  dealForm.value = {
    id: null, stageId, contactId: "", title: "", value: 0, priority: "media",
    probability: null, commission: 0, notes: "", products: []
  };
  dealFiles.value = [];
  dealQuotes.value = [];
  isEditDeal.value = false;
  showDealModal.value = true;
}

async function abrirEditarNegocio(deal) {
  dealForm.value = {
    id: deal.id,
    stageId: deal.stage_id,
    contactId: deal.contact_id || "",
    title: deal.title,
    value: deal.value,
    priority: deal.priority,
    probability: deal.probability,
    commission: deal.commission,
    notes: deal.notes || "",
    products: deal.products ? JSON.parse(JSON.stringify(deal.products)) : []
  };
  isEditDeal.value = true;
  showDealModal.value = true;

  const [filesRes, quotesRes] = await Promise.all([
    api.get(`/crm/deals/${deal.id}/files`),
    api.get(`/crm/deals/${deal.id}/quotes`)
  ]);
  dealFiles.value = filesRes.data.files;
  dealQuotes.value = quotesRes.data.quotes;
}

function agregarProducto() {
  dealForm.value.products.push({ name: "", quantity: 1, price: 0 });
}

function quitarProducto(idx) {
  dealForm.value.products.splice(idx, 1);
}

const totalProductos = computed(() => {
  return dealForm.value.products.reduce((suma, p) => suma + (Number(p.quantity) || 0) * (Number(p.price) || 0), 0);
});

async function agregarArchivo() {
  if (!nuevoArchivo.value.name.trim() || !nuevoArchivo.value.url.trim()) return;

  const res = await api.post(`/crm/deals/${dealForm.value.id}/files`, nuevoArchivo.value);
  dealFiles.value.unshift(res.data.file);
  nuevoArchivo.value = { name: "", url: "" };
}

async function eliminarArchivo(file) {
  await api.delete(`/crm/deal-files/${file.id}`);
  dealFiles.value = dealFiles.value.filter(f => f.id !== file.id);
}

async function guardarNegocio() {
  if (!dealForm.value.title.trim()) {
    Swal.fire({ icon: "warning", title: "El título del negocio es obligatorio" });
    return;
  }

  guardandoDeal.value = true;

  const payloadComun = {
    title: dealForm.value.title,
    value: dealForm.value.value,
    priority: dealForm.value.priority,
    probability: dealForm.value.probability,
    commission: dealForm.value.commission,
    notes: dealForm.value.notes,
    products: dealForm.value.products
  };

  try {
    if (isEditDeal.value) {
      await api.put(`/crm/deals/${dealForm.value.id}`, payloadComun);
    } else {
      await api.post("/crm/deals", {
        ...payloadComun,
        pipelineId: selectedPipelineId.value,
        stageId: dealForm.value.stageId,
        contactId: dealForm.value.contactId || null
      });
    }

    showDealModal.value = false;
    await cargar();

  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo guardar el negocio",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    });
  } finally {
    guardandoDeal.value = false;
  }
}

async function eliminarNegocio() {
  const { isConfirmed } = await Swal.fire({
    icon: "warning",
    title: "¿Eliminar este negocio?",
    showCancelButton: true,
    confirmButtonText: "Sí, eliminar",
    cancelButtonText: "Cancelar",
    confirmButtonColor: "#f5365c"
  });

  if (!isConfirmed) return;

  await api.delete(`/crm/deals/${dealForm.value.id}`);
  showDealModal.value = false;
  await cargar();
}

async function crearEtapa() {
  if (!nuevaEtapa.value.trim()) return;

  await api.post("/crm/pipeline/stages", { pipelineId: selectedPipelineId.value, name: nuevaEtapa.value });
  nuevaEtapa.value = "";
  await cargar();
}

async function renombrarEtapa(stage) {
  const { value: nuevoNombre } = await Swal.fire({
    title: "Renombrar etapa",
    input: "text",
    inputValue: stage.name,
    showCancelButton: true,
    confirmButtonText: "Guardar",
    cancelButtonText: "Cancelar"
  });

  if (!nuevoNombre || nuevoNombre === stage.name) return;

  await api.put(`/crm/pipeline/stages/${stage.id}`, { name: nuevoNombre });
  await cargar();
}

async function moverEtapa(stage, direccion) {
  const idx = stages.value.findIndex(s => s.id === stage.id);
  const otro = stages.value[idx + direccion];
  if (!otro) return;

  await Promise.all([
    api.put(`/crm/pipeline/stages/${stage.id}`, { position: otro.position }),
    api.put(`/crm/pipeline/stages/${otro.id}`, { position: stage.position })
  ]);

  await cargar();
}

async function eliminarEtapa(stage) {
  const { isConfirmed } = await Swal.fire({
    icon: "warning",
    title: `¿Eliminar la etapa "${stage.name}"?`,
    showCancelButton: true,
    confirmButtonText: "Sí, eliminar",
    cancelButtonText: "Cancelar",
    confirmButtonColor: "#f5365c"
  });

  if (!isConfirmed) return;

  try {
    await api.delete(`/crm/pipeline/stages/${stage.id}`);
    await cargar();

  } catch (err) {
    if (err.response?.status === 400 && err.response.data?.error?.includes("negocio")) {
      const opciones = {};
      stages.value.filter(s => s.id !== stage.id).forEach(s => { opciones[s.id] = s.name; });

      if (Object.keys(opciones).length === 0) {
        Swal.fire({ icon: "error", title: "No se puede eliminar", text: "Es la única etapa disponible para reasignar los negocios." });
        return;
      }

      const { value: destino } = await Swal.fire({
        icon: "question",
        title: err.response.data.error,
        input: "select",
        inputOptions: opciones,
        showCancelButton: true,
        confirmButtonText: "Mover y eliminar",
        cancelButtonText: "Cancelar"
      });

      if (!destino) return;

      await api.delete(`/crm/pipeline/stages/${stage.id}`, { data: { reassignStageId: Number(destino) } });
      await cargar();

    } else {
      Swal.fire({
        icon: "error",
        title: "No se pudo eliminar la etapa",
        text: err.response?.data?.error || "Error de conexión con el servidor."
      });
    }
  }
}

async function crearMotivo() {
  if (!nuevoMotivo.value.trim()) return;

  await api.post("/crm/pipeline/loss-reasons", { name: nuevoMotivo.value });
  nuevoMotivo.value = "";
  await cargar();
}

async function eliminarMotivo(reason) {
  const { isConfirmed } = await Swal.fire({
    icon: "warning",
    title: `¿Eliminar el motivo "${reason.name}"?`,
    showCancelButton: true,
    confirmButtonText: "Sí, eliminar",
    cancelButtonText: "Cancelar",
    confirmButtonColor: "#f5365c"
  });

  if (!isConfirmed) return;

  try {
    await api.delete(`/crm/pipeline/loss-reasons/${reason.id}`);
    await cargar();
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo eliminar el motivo",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    });
  }
}

async function crearAutomatizacion() {
  const { stageId, actionType, message, title, dueInDays } = nuevaAutomatizacion.value;

  if (!stageId) {
    Swal.fire({ icon: "warning", title: "Selecciona una etapa" });
    return;
  }

  const config = actionType === "send_whatsapp_template"
    ? { message }
    : { title, dueInDays: Number(dueInDays) || 1 };

  try {
    await api.post("/crm/pipeline/automations", { stageId: Number(stageId), actionType, config });
    nuevaAutomatizacion.value = { stageId: "", actionType: "create_task", message: "", title: "", dueInDays: 1 };
    await cargar();
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo crear la automatización",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    });
  }
}

async function toggleAutomatizacion(automation) {
  await api.put(`/crm/pipeline/automations/${automation.id}`, { active: !automation.active });
  await cargar();
}

async function eliminarAutomatizacion(automation) {
  const { isConfirmed } = await Swal.fire({
    icon: "warning",
    title: "¿Eliminar esta automatización?",
    showCancelButton: true,
    confirmButtonText: "Sí, eliminar",
    cancelButtonText: "Cancelar",
    confirmButtonColor: "#f5365c"
  });

  if (!isConfirmed) return;

  await api.delete(`/crm/pipeline/automations/${automation.id}`);
  await cargar();
}

async function crearPipeline() {
  if (!nuevoPipelineName.value.trim()) return;

  const res = await api.post("/crm/pipelines", { name: nuevoPipelineName.value });
  nuevoPipelineName.value = "";
  await cargarPipelines();
  selectedPipelineId.value = res.data.pipeline.id;
}

async function eliminarPipeline(pipeline) {
  const { isConfirmed } = await Swal.fire({
    icon: "warning",
    title: `¿Eliminar el pipeline "${pipeline.name}"?`,
    text: "Debe estar vacío de etapas.",
    showCancelButton: true,
    confirmButtonText: "Sí, eliminar",
    cancelButtonText: "Cancelar",
    confirmButtonColor: "#f5365c"
  });

  if (!isConfirmed) return;

  try {
    await api.delete(`/crm/pipelines/${pipeline.id}`);
    selectedPipelineId.value = null;
    await cargarPipelines();
    await cargar();
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo eliminar el pipeline",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    });
  }
}

watch(selectedPipelineId, async () => {
  await cargar();
});

onMounted(async () => {
  cargando.value = true;
  try {
    await cargarPipelines();
    await Promise.all([cargar(), cargarContactos()]);
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo cargar el embudo",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    });
  } finally {
    cargando.value = false;
  }
});
</script>

<template>
  <div class="container-fluid py-4">

    <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
      <div class="d-flex align-items-center gap-2">
        <h4 class="mb-0" style="color: white;">
          <i class="bi bi-kanban me-2"></i>
          Embudo de ventas
        </h4>

        <select v-model="selectedPipelineId" class="form-select form-select-sm pipeline-select">
          <option v-for="p in pipelines" :key="p.id" :value="p.id">{{ p.name }}</option>
        </select>

        <button class="btn btn-sm btn-light btn-icon-only mb-0" title="Gestionar pipelines" @click="showPipelinesModal = true">
          <i class="bi bi-gear"></i>
        </button>
      </div>

      <argon-button color="secondary" @click="showStagesModal = true">
        <i class="bi bi-columns-gap me-1"></i>
        Configurar etapas
      </argon-button>
    </div>

    <div v-if="cargando" class="text-center py-5">
      <div class="spinner-border text-success" role="status"></div>
    </div>

    <div v-else-if="stats" class="row g-2 mb-3">
      <div class="col-6 col-md">
        <div class="card text-center py-2 h-100">
          <div class="small text-muted">Abiertos</div>
          <div class="fw-bold">{{ stats.cantidadAbiertos }}</div>
        </div>
      </div>
      <div class="col-6 col-md">
        <div class="card text-center py-2 h-100">
          <div class="small text-muted">Valor abierto</div>
          <div class="fw-bold">{{ formatMoneda(stats.valorAbierto) }}</div>
        </div>
      </div>
      <div class="col-6 col-md">
        <div class="card text-center py-2 h-100">
          <div class="small text-muted">Ganado este mes</div>
          <div class="fw-bold text-success">{{ formatMoneda(stats.ganadoMes) }}</div>
        </div>
      </div>
      <div class="col-6 col-md">
        <div class="card text-center py-2 h-100">
          <div class="small text-muted">Perdido este mes</div>
          <div class="fw-bold text-danger">{{ formatMoneda(stats.perdidoMes) }}</div>
        </div>
      </div>
      <div class="col-6 col-md">
        <div class="card text-center py-2 h-100">
          <div class="small text-muted">Tasa de conversión</div>
          <div class="fw-bold">{{ stats.tasaConversion }}%</div>
        </div>
      </div>
    </div>

    <div v-if="!cargando" class="pipeline-scroll d-flex gap-3">
      <div v-for="stage in stages" :key="stage.id" class="pipeline-column card">
        <div
          class="card-header d-flex justify-content-between align-items-center"
          :class="{ 'bg-success-subtle': stage.is_won, 'bg-danger-subtle': stage.is_lost }"
        >
          <strong>{{ stage.name }}</strong>
          <span class="badge bg-gradient-secondary">{{ dealsByStage[stage.id]?.length || 0 }}</span>
        </div>

        <div class="card-body pipeline-column-body d-flex flex-column">
          <draggable
            v-model="dealsByStage[stage.id]"
            :group="{ name: 'negocios' }"
            item-key="id"
            class="pipeline-list"
            @change="onChange(stage.id, $event)"
          >
            <template #item="{ element }">
              <div class="pipeline-card card mb-2" @click="abrirEditarNegocio(element)">
                <div class="card-body p-2">
                  <div class="fw-bold">{{ element.title }}</div>
                  <div class="text-muted small">
                    {{ element.contact_name || "Sin contacto" }}
                  </div>
                  <div class="d-flex justify-content-between align-items-center mt-1">
                    <span class="small text-success fw-bold">{{ formatMoneda(element.value) }}</span>
                    <span
                      class="badge"
                      :class="{
                        'bg-danger': element.priority === 'alta',
                        'bg-warning text-dark': element.priority === 'media',
                        'bg-gradient-secondary': element.priority === 'baja'
                      }"
                    >
                      {{ element.priority }}
                    </span>
                  </div>
                </div>
              </div>
            </template>
          </draggable>

          <button class="btn btn-sm btn-outline-secondary w-100 mt-2" @click="abrirNuevoNegocio(stage.id)">
            <i class="bi bi-plus-circle me-1"></i>Negocio
          </button>
        </div>
      </div>
    </div>

    <!-- MODAL NEGOCIO (crear/editar) -->
    <div v-if="showDealModal" class="modal fade show d-block">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">

          <div class="modal-header">
            <h5>{{ isEditDeal ? "Editar negocio" : "Nuevo negocio" }}</h5>
            <button class="btn-close" @click="showDealModal = false"></button>
          </div>

          <div class="modal-body">
            <div class="mb-2">
              <label class="form-label small">Título</label>
              <argon-input v-model="dealForm.title" placeholder="Viaje a Punta Cana" />
            </div>

            <div class="mb-2">
              <label class="form-label small">Contacto</label>
              <select v-model="dealForm.contactId" class="form-select" :disabled="isEditDeal">
                <option value="">Sin contacto</option>
                <option v-for="c in contactos" :key="c.id" :value="c.id">{{ c.name }} {{ c.last_name || "" }}</option>
              </select>
            </div>

            <div class="row g-2">
              <div class="col-md-3">
                <label class="form-label small">Valor esperado (COP)</label>
                <argon-input v-model="dealForm.value" type="number" />
              </div>
              <div class="col-md-3">
                <label class="form-label small">Prioridad</label>
                <select v-model="dealForm.priority" class="form-select">
                  <option value="baja">Baja</option>
                  <option value="media">Media</option>
                  <option value="alta">Alta</option>
                </select>
              </div>
              <div class="col-md-3">
                <label class="form-label small">Probabilidad (%)</label>
                <argon-input v-model="dealForm.probability" type="number" min="0" max="100" placeholder="según etapa" />
              </div>
              <div class="col-md-3">
                <label class="form-label small">Comisión (COP)</label>
                <argon-input v-model="dealForm.commission" type="number" />
              </div>
            </div>

            <div class="mt-3">
              <label class="form-label small">Notas</label>
              <textarea v-model="dealForm.notes" class="form-control" rows="2"></textarea>
            </div>

            <!-- PRODUCTOS -->
            <div class="mt-3">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <label class="form-label small mb-0">Productos</label>
                <button class="btn btn-sm btn-link p-0" @click="agregarProducto">+ Agregar</button>
              </div>
              <div v-for="(p, idx) in dealForm.products" :key="idx" class="row g-1 mb-1 align-items-center">
                <div class="col-5"><argon-input v-model="p.name" placeholder="Producto" /></div>
                <div class="col-2"><argon-input v-model="p.quantity" type="number" placeholder="Cant." /></div>
                <div class="col-3"><argon-input v-model="p.price" type="number" placeholder="Precio" /></div>
                <div class="col-2">
                  <button class="btn btn-sm btn-outline-danger" @click="quitarProducto(idx)"><i class="bi bi-trash"></i></button>
                </div>
              </div>
              <div v-if="dealForm.products.length > 0" class="small text-muted text-end">
                Total productos: {{ formatMoneda(totalProductos) }}
              </div>
            </div>

            <!-- ARCHIVOS Y COTIZACIONES: solo al editar (necesitan un id ya creado) -->
            <template v-if="isEditDeal">
              <div class="mt-3">
                <label class="form-label small">Archivos</label>
                <ul class="list-group mb-2">
                  <li v-for="f in dealFiles" :key="f.id" class="list-group-item d-flex justify-content-between align-items-center py-1">
                    <a :href="f.url" target="_blank">{{ f.name }}</a>
                    <button class="btn btn-sm btn-outline-danger" @click="eliminarArchivo(f)"><i class="bi bi-trash"></i></button>
                  </li>
                  <li v-if="dealFiles.length === 0" class="list-group-item text-muted small py-1">Sin archivos.</li>
                </ul>
                <div class="d-flex gap-1">
                  <argon-input v-model="nuevoArchivo.name" placeholder="Nombre" />
                  <argon-input v-model="nuevoArchivo.url" placeholder="URL" />
                  <button class="btn btn-sm btn-outline-secondary" @click="agregarArchivo"><i class="bi bi-plus-circle"></i></button>
                </div>
              </div>

              <div class="mt-3" v-if="dealQuotes.length > 0">
                <label class="form-label small">Cotizaciones del contacto</label>
                <ul class="list-group">
                  <li v-for="q in dealQuotes" :key="q.id" class="list-group-item d-flex justify-content-between py-1 small">
                    <span>{{ q.destination }} — {{ q.quote_code }}</span>
                    <a v-if="q.pdf_url" :href="q.pdf_url" target="_blank">PDF</a>
                  </li>
                </ul>
              </div>
            </template>

            <button
              v-if="isEditDeal && dealForm.contactId"
              class="btn btn-sm btn-outline-primary mt-3"
              @click="verContacto(dealForm.contactId)"
            >
              <i class="bi bi-person-lines-fill me-1"></i>Ver contacto
            </button>
          </div>

          <div class="modal-footer justify-content-between">
            <button v-if="isEditDeal" class="btn btn-outline-danger" @click="eliminarNegocio">
              <i class="bi bi-trash me-1"></i>Eliminar
            </button>
            <div class="d-flex gap-2 ms-auto">
              <button class="btn btn-secondary" @click="showDealModal = false">Cancelar</button>
              <argon-button color="success" :disabled="guardandoDeal" @click="guardarNegocio">Guardar</argon-button>
            </div>
          </div>

        </div>
      </div>
    </div>

    <div v-if="showDealModal" class="modal-backdrop fade show"></div>

    <!-- MODAL PIPELINES -->
    <div v-if="showPipelinesModal" class="modal fade show d-block">
      <div class="modal-dialog">
        <div class="modal-content">

          <div class="modal-header">
            <h5><i class="bi bi-diagram-3 me-1"></i>Pipelines</h5>
            <button class="btn-close" @click="showPipelinesModal = false"></button>
          </div>

          <div class="modal-body">
            <ul class="list-group mb-3">
              <li v-for="p in pipelines" :key="p.id" class="list-group-item d-flex justify-content-between align-items-center">
                {{ p.name }}
                <button class="btn btn-sm btn-danger" :disabled="pipelines.length === 1" @click="eliminarPipeline(p)">
                  <i class="bi bi-trash"></i>
                </button>
              </li>
            </ul>

            <div class="d-flex gap-2">
              <argon-input v-model="nuevoPipelineName" placeholder="Nombre del nuevo pipeline..." />
              <argon-button color="success" @click="crearPipeline">
                <i class="bi bi-plus-circle"></i>
              </argon-button>
            </div>
          </div>

          <div class="modal-footer">
            <button class="btn btn-secondary" @click="showPipelinesModal = false">Cerrar</button>
          </div>

        </div>
      </div>
    </div>

    <div v-if="showPipelinesModal" class="modal-backdrop fade show"></div>

    <!-- MODAL CONFIGURAR ETAPAS -->
    <div v-if="showStagesModal" class="modal fade show d-block">
      <div class="modal-dialog">
        <div class="modal-content">

          <div class="modal-header">
            <h5><i class="bi bi-gear"></i> Configurar etapas</h5>
            <button class="btn-close" @click="showStagesModal = false"></button>
          </div>

          <div class="modal-body">

            <ul class="list-group mb-3">
              <li v-for="(stage, i) in stages" :key="stage.id" class="list-group-item d-flex justify-content-between align-items-center">
                <span>
                  {{ stage.name }}
                  <span v-if="stage.is_won" class="badge bg-success ms-1">Ganado</span>
                  <span v-if="stage.is_lost" class="badge bg-danger ms-1">Perdido</span>
                </span>

                <div>
                  <button class="btn btn-sm btn-light" :disabled="i === 0" @click="moverEtapa(stage, -1)">
                    <i class="bi bi-arrow-up"></i>
                  </button>
                  <button class="btn btn-sm btn-light" :disabled="i === stages.length - 1" @click="moverEtapa(stage, 1)">
                    <i class="bi bi-arrow-down"></i>
                  </button>
                  <button class="btn btn-sm btn-warning" @click="renombrarEtapa(stage)">
                    <i class="bi bi-pencil"></i>
                  </button>
                  <button class="btn btn-sm btn-danger" @click="eliminarEtapa(stage)">
                    <i class="bi bi-trash"></i>
                  </button>
                </div>
              </li>
            </ul>

            <div class="d-flex gap-2">
              <argon-input v-model="nuevaEtapa" placeholder="Nombre de la nueva etapa..." />
              <argon-button color="success" @click="crearEtapa">
                <i class="bi bi-plus-circle"></i>
              </argon-button>
            </div>

          </div>

          <div class="modal-footer justify-content-between align-items-center flex-wrap gap-2">
            <div class="d-flex gap-2">
              <button class="btn btn-outline-danger btn-sm" @click="showStagesModal = false; showLossReasonsModal = true">
                <i class="bi bi-x-octagon me-1"></i>Motivos de pérdida
              </button>
              <button class="btn btn-outline-primary btn-sm" @click="showStagesModal = false; showAutomationsModal = true">
                <i class="bi bi-lightning-charge me-1"></i>Automatizaciones
              </button>
            </div>
            <button class="btn btn-secondary btn-sm" @click="showStagesModal = false">Cerrar</button>
          </div>

        </div>
      </div>
    </div>

    <div v-if="showStagesModal" class="modal-backdrop fade show"></div>

    <!-- MODAL MOTIVOS DE PÉRDIDA -->
    <div v-if="showLossReasonsModal" class="modal fade show d-block">
      <div class="modal-dialog">
        <div class="modal-content">

          <div class="modal-header">
            <h5><i class="bi bi-x-octagon"></i> Motivos de pérdida</h5>
            <button class="btn-close" @click="showLossReasonsModal = false"></button>
          </div>

          <div class="modal-body">
            <ul class="list-group mb-3">
              <li v-for="reason in lossReasons" :key="reason.id" class="list-group-item d-flex justify-content-between align-items-center">
                {{ reason.name }}
                <button class="btn btn-sm btn-danger" @click="eliminarMotivo(reason)">
                  <i class="bi bi-trash"></i>
                </button>
              </li>
              <li v-if="lossReasons.length === 0" class="list-group-item text-muted">
                Sin motivos configurados.
              </li>
            </ul>

            <div class="d-flex gap-2">
              <argon-input v-model="nuevoMotivo" placeholder="Nombre del nuevo motivo..." />
              <argon-button color="success" @click="crearMotivo">
                <i class="bi bi-plus-circle"></i>
              </argon-button>
            </div>
          </div>

          <div class="modal-footer">
            <button class="btn btn-secondary" @click="showLossReasonsModal = false; showStagesModal = true">
              Volver
            </button>
          </div>

        </div>
      </div>
    </div>

    <div v-if="showLossReasonsModal" class="modal-backdrop fade show"></div>

    <!-- MODAL AUTOMATIZACIONES -->
    <div v-if="showAutomationsModal" class="modal fade show d-block">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">

          <div class="modal-header">
            <h5><i class="bi bi-lightning-charge"></i> Automatizaciones por etapa</h5>
            <button class="btn-close" @click="showAutomationsModal = false"></button>
          </div>

          <div class="modal-body">
            <p class="text-muted small">
              Cuando un negocio entra a una etapa, se ejecutan automáticamente las acciones activas configuradas para ella.
              Usa <code v-pre>{{name}}</code> en el mensaje o título para incluir el nombre del contacto.
            </p>

            <ul class="list-group mb-4">
              <li v-for="automation in automationsDelPipeline" :key="automation.id" class="list-group-item">
                <div class="d-flex justify-content-between align-items-start">
                  <div>
                    <span class="badge bg-gradient-secondary me-1">{{ automation.stage_name }}</span>
                    <span class="badge" :class="automation.action_type === 'create_task' ? 'bg-info text-dark' : 'bg-success'">
                      {{ automation.action_type === 'create_task' ? 'Crear tarea' : 'Enviar WhatsApp' }}
                    </span>
                    <div class="small text-muted mt-1">
                      <span v-if="automation.action_type === 'create_task'">
                        "{{ automation.config.title }}" — vence en {{ automation.config.dueInDays }} día(s)
                      </span>
                      <span v-else>"{{ automation.config.message }}"</span>
                    </div>
                  </div>
                  <div class="d-flex gap-1">
                    <div class="form-check form-switch me-1">
                      <input
                        class="form-check-input"
                        type="checkbox"
                        role="switch"
                        :checked="automation.active"
                        @change="toggleAutomatizacion(automation)"
                      >
                    </div>
                    <button class="btn btn-sm btn-danger" @click="eliminarAutomatizacion(automation)">
                      <i class="bi bi-trash"></i>
                    </button>
                  </div>
                </div>
              </li>
              <li v-if="automationsDelPipeline.length === 0" class="list-group-item text-muted">
                Sin automatizaciones configuradas.
              </li>
            </ul>

            <h6>Nueva automatización</h6>
            <div class="row g-2 align-items-end">
              <div class="col-md-4">
                <label class="form-label small">Etapa</label>
                <select v-model="nuevaAutomatizacion.stageId" class="form-select">
                  <option value="" disabled>Selecciona...</option>
                  <option v-for="stage in stages" :key="stage.id" :value="stage.id">{{ stage.name }}</option>
                </select>
              </div>

              <div class="col-md-4">
                <label class="form-label small">Acción</label>
                <select v-model="nuevaAutomatizacion.actionType" class="form-select">
                  <option value="create_task">Crear tarea</option>
                  <option value="send_whatsapp_template">Enviar WhatsApp</option>
                </select>
              </div>

              <template v-if="nuevaAutomatizacion.actionType === 'create_task'">
                <div class="col-md-6">
                  <label class="form-label small">Título de la tarea</label>
                  <argon-input v-model="nuevaAutomatizacion.title" placeholder="Llamar a {{name}}" />
                </div>
                <div class="col-md-2">
                  <label class="form-label small">Vence en (días)</label>
                  <argon-input v-model="nuevaAutomatizacion.dueInDays" type="number" min="0" />
                </div>
              </template>

              <template v-else>
                <div class="col-md-8">
                  <label class="form-label small">Mensaje</label>
                  <argon-input v-model="nuevaAutomatizacion.message" placeholder="Hola {{name}}, ..." />
                </div>
              </template>

              <div class="col-12">
                <argon-button color="success" class="w-100" @click="crearAutomatizacion">
                  <i class="bi bi-plus-circle me-1"></i>Agregar automatización
                </argon-button>
              </div>
            </div>
          </div>

          <div class="modal-footer">
            <button class="btn btn-secondary" @click="showAutomationsModal = false; showStagesModal = true">
              Volver
            </button>
          </div>

        </div>
      </div>
    </div>

    <div v-if="showAutomationsModal" class="modal-backdrop fade show"></div>

  </div>
</template>

<style scoped>
.pipeline-select {
  width: auto;
  min-width: 160px;
}

.pipeline-scroll {
  overflow-x: auto;
  padding-bottom: 1rem;
  align-items: stretch;
  min-height: calc(100vh - 340px);
}

.pipeline-column {
  flex: 1 1 280px;
  min-width: 240px;
  display: flex;
  flex-direction: column;
}

@media (max-width: 575.98px) {
  .pipeline-column {
    flex: 0 0 85vw;
    min-width: 85vw;
    max-width: 85vw;
  }
}

.pipeline-column-body {
  flex: 1 1 auto;
  min-height: 300px;
  overflow-y: auto;
}

.pipeline-list {
  flex: 1 1 auto;
  min-height: 100%;
}

.pipeline-card {
  cursor: pointer;
  border: 1px solid #e9ecef;
}

.pipeline-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}
</style>
